import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BuddyLogo } from "./Brand";
import { useAuth } from "../lib/auth";
import { homeForRole } from "../lib/roles";
import { Button } from "./ui/button";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationBell from "./NotificationBell";
import {
  LayoutDashboard, Camera, MessageCircle, GraduationCap,
  Users, School, ShieldCheck, LogOut, Bell, Settings, BarChart3,
  BookOpen, ClipboardList, Award, Accessibility as AccessibilityIcon,
} from "lucide-react";

const NAV_BY_ROLE = {
  student: [
    { to: "/app/student", labelKey: "nav.dashboard", icon: LayoutDashboard, testid: "nav-student-dashboard" },
    { to: "/app/student/scan", labelKey: "nav.scanHomework", icon: Camera, testid: "nav-student-scan" },
    { to: "/app/student/ask", labelKey: "nav.askLumi", icon: MessageCircle, testid: "nav-student-ask" },
    { to: "/app/student/assignments", labelKey: "nav.assignments", icon: ClipboardList, testid: "nav-student-assignments" },
    { to: "/app/student/progress", labelKey: "nav.progress", icon: BarChart3, testid: "nav-student-progress" },
    { to: "/app/student/rewards", labelKey: "nav.rewards", icon: Award, testid: "nav-student-rewards" },
  ],
  parent: [
    { to: "/app/parent", labelKey: "nav.dashboard", icon: LayoutDashboard, testid: "nav-parent-dashboard" },
    { to: "/app/parent/subscription", labelKey: "nav.subscription", icon: ShieldCheck, testid: "nav-parent-subscription" },
    { to: "/app/parent/settings", labelKey: "common.settings", icon: Settings, testid: "nav-parent-settings" },
  ],
  teacher: [
    { to: "/app/teacher", labelKey: "nav.classes", icon: GraduationCap, testid: "nav-teacher-classes" },
    { to: "/app/teacher/assignments", labelKey: "nav.assignments", icon: ClipboardList, testid: "nav-teacher-assignments" },
  ],
  school_admin: [
    { to: "/app/school", labelKey: "nav.schoolOverview", icon: School, testid: "nav-school-overview" },
  ],
  platform_admin: [
    { to: "/app/admin", labelKey: "nav.system", icon: LayoutDashboard, testid: "nav-admin-system" },
    { to: "/app/admin/activations", labelKey: "nav.tabletActivations", icon: BookOpen, testid: "nav-admin-activations" },
  ],
};

export default function AppShell({ children, isStudent = false }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const items = user ? NAV_BY_ROLE[user.role] || [] : [];

  return (
    <div className={isStudent ? "student-bg min-h-screen" : "adult-bg min-h-screen"}>
      <header className="hb-glass sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
          <Link to={user ? homeForRole(user.role) : "/"} data-testid="header-logo-link" className="shrink-0 min-w-0">
            <BuddyLogo size={36} />
          </Link>
          <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {items.map((it) => {
              const Icon = it.icon;
              const active = loc.pathname === it.to;
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  data-testid={it.testid}
                  className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors whitespace-nowrap ${
                    active
                      ? "bg-slate-900 text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {t(it.labelKey)}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            <LanguageSwitcher />
            {user && <NotificationBell />}
            <Link to="/app/accessibility" title={t("nav.accessibility")} data-testid="nav-accessibility"
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-700">
              <AccessibilityIcon className="w-5 h-5" />
            </Link>
            {user && (
              <>
                <div className="hidden 2xl:block text-right leading-tight">
                  <div className="text-sm font-semibold text-slate-900 max-w-[160px] truncate">{user.full_name}</div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">{t(`roles.${user.role}`)}</div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="btn-logout"
                  onClick={() => { logout(); nav("/"); }}
                >
                  <LogOut className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">{t("common.signOut")}</span>
                </Button>
              </>
            )}
          </div>
        </div>
        {/* Compact horizontal-scroll nav for < lg breakpoint (phones + tablet portrait) */}
        {items.length > 0 && (
          <div className="lg:hidden overflow-x-auto no-scrollbar border-t border-slate-200">
            <div className="flex gap-1 px-3 py-2 min-w-max">
              {items.map((it) => {
                const active = loc.pathname === it.to;
                const Icon = it.icon;
                return (
                  <Link key={it.to} to={it.to}
                    data-testid={it.testid + "-mobile"}
                    className={`whitespace-nowrap px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1 ${
                      active ? "bg-slate-900 text-white" : "text-slate-700 bg-slate-100"
                    }`}>
                    <Icon className="w-3.5 h-3.5" />
                    {t(it.labelKey)}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </header>
      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 py-6 sm:py-10">{children}</main>
    </div>
  );
}
